import BigNumber from 'bignumber.js'
import { TaskNameId, _1e6 } from 'constants/constants'
import { ONE, PERCENT, ZERO } from 'constants/number'
import { MarketInfo } from 'interfaces/pool.interface'
import { useEffect } from 'react'
import { assetService } from 'services/assetService'
import { irmService } from 'services/irmService'
import { poolService } from 'services/poolService'
import { oracleStore, poolStore, taskStore } from 'stores'
import { apr2apy } from 'utils/calculateAPY'
import { toAssetInfo } from 'utils/contractParse'
import simulateIRM, { convertMemoToCalKind } from 'utils/simulateIRM'
import { useOraclePrice } from './useOracle'

export const usePool = () => {
  const { assets } = assetService
  const poolAddr = poolService.poolAddress
  const prices = oracleStore.getPrices(poolService.poolAddress)

  useOraclePrice({ poolAddr })

  useEffect(() => {
    const fetchData = async () => {
      if (!prices) return
      const [supplyMarketList, activeMarket, accountStatus, poolConfig] =
        await Promise.all([
          poolService.getMarketList(),
          poolService.getActiveMarkets(),
          poolService.getAccountStatus(),
          poolService.getConfig(),
        ])
      const irmRoutes = await irmService.getRoutes(poolConfig.irm_contract)
      const configs = await Promise.all(
        supplyMarketList.map(async (market) => {
          return {
            underlying: market.underlying,
            config: await poolService.getMarketConfig(
              toAssetInfo(market.underlying),
            ),
          }
        }),
      )

      const { collateral_value, debt_value } = accountStatus
      const walletCollateralValue = new BigNumber(collateral_value)
      const walletTotalDebtValue = new BigNumber(debt_value)
      const userBorrowingPower =
        walletCollateralValue.minus(walletTotalDebtValue)
      const walletLTV = walletTotalDebtValue
        .div(walletCollateralValue)
        .times(PERCENT)

      const borrowMarketList = supplyMarketList.filter(
        (market) => market.borrowable,
      )

      let marketInfo: Record<string, MarketInfo> = {}
      let accumulateNetAPY = ZERO

      // Load irm config & eToken rate
      await Promise.all(
        supplyMarketList.map(async (market) => {
          const route = irmRoutes[market.underlying]
          // rate = underlyingSupply / eTokenSupply
          const [irmConfig, rate] = await Promise.all([
            irmService.getIRMConfig(route.irm_contract),
            poolService.getExchangeRate(toAssetInfo(market.etoken_addr)),
          ])
          marketInfo[market.underlying] = {
            ...marketInfo[market.underlying],
            irmRoute: route,
            irmConfig: irmConfig,
            rateU2E: ONE.div(rate), // = eTokenSupply / underlyingSupply
          }
        }),
      )

      configs.forEach((c) => {
        marketInfo[c.underlying] = {
          ...marketInfo[c.underlying],
          config: c.config,
        }
      })

      supplyMarketList.forEach((market) => {
        // set default value
        const { underlying } = market

        marketInfo[underlying] = {
          ...marketInfo[underlying],
          walletSupplyBalance: ZERO,
          locked: false,
          walletBorrowBalance: ZERO,
          totalCredit: new BigNumber(market.total_credit).div(
            `1e${assets[underlying].decimals}`,
          ),
          totalLoan: new BigNumber(market.total_loan).div(
            `1e${assets[underlying].decimals}`,
          ),
        }

        const utilization = new BigNumber(market.total_loan).div(
          new BigNumber(market.total_credit).plus(market.total_loan),
        )

        const { supplyAPR: _supplyAPR, borrowAPR: _borrowAPR } = simulateIRM[
          convertMemoToCalKind[marketInfo[underlying].irmRoute.memo]
        ](
          parseFloat(poolConfig.fee_rate),
          utilization,
          marketInfo[underlying].irmConfig as any,
        )

        // const utilization = new BigNumber(_utilization)
        // const supplyAPY = new BigNumber(_supplyAPR)
        // const borrowAPY = new BigNumber(_borrowAPR)
        const supplyAPY = apr2apy(new BigNumber(_supplyAPR / 100))
        const borrowAPY = apr2apy(new BigNumber(_borrowAPR / 100))

        const borrowLimitAmount = userBorrowingPower
          .div(_1e6) // div uusd oracle
          .times(new BigNumber(market.config.borrow_factor))
          .div(prices[underlying])
        const borrowLimit = BigNumber.max(
          BigNumber.min(
            borrowLimitAmount,
            new BigNumber(market.total_credit).div(_1e6),
          ),
          0,
        )

        marketInfo[underlying] = {
          ...marketInfo[underlying],
          supplyAPY,
          utilization: utilization.isNaN() ? ZERO : utilization,
          borrowAPY,
          borrowLimit,
        }
      })

      // set for specific account
      activeMarket.forEach((account) => {
        const { collateral_amount, debt_amount, locked, underlying } = account
        const { supplyAPY, borrowAPY } = marketInfo[underlying]

        const walletSupplyBalance = new BigNumber(collateral_amount).div(
          new BigNumber(`1e${assets[underlying].decimals}`),
        )

        const walletBorrowBalance = new BigNumber(debt_amount).div(
          `1e${assets[underlying].decimals}`,
        )

        accumulateNetAPY = accumulateNetAPY
          .plus(walletSupplyBalance.times(supplyAPY).times(prices[underlying]))
          .minus(walletBorrowBalance.times(borrowAPY).times(prices[underlying]))

        marketInfo[underlying] = {
          ...marketInfo[underlying],
          walletSupplyBalance,
          walletBorrowBalance,
          locked,
        }
      })

      const walletAllSupplyBalUSD = BigNumber.sum(
        ...activeMarket.map((account) =>
          new BigNumber(account.collateral_amount)
            .times(prices ? prices[account.underlying] : 0)
            .div(
              new BigNumber(
                `1e${assetService.assets[account.underlying].decimals}`,
              ),
            ),
        ),
        0,
      )

      const walletAllBorrowBalUSD = BigNumber.sum(
        ...activeMarket.map((account) =>
          new BigNumber(account.debt_amount)
            .times(prices[account.underlying])
            .div(
              new BigNumber(
                `1e${assetService.assets[account.underlying].decimals}`,
              ),
            ),
        ),
        0,
      )

      const totalSuppliedUSD = BigNumber.sum(
        ...supplyMarketList.map((market) =>
          new BigNumber(market.total_credit)
            .plus(market.total_loan)
            .times(prices[market.underlying]),
        ),
        0,
      )

      const totalBorrowedUSD = BigNumber.sum(
        ...borrowMarketList.map((market) =>
          new BigNumber(market.total_loan).times(prices[market.underlying]),
        ),
        0,
      )

      const walletNetAPY = accumulateNetAPY.div(walletAllSupplyBalUSD)
      poolStore.setPool(poolAddr, {
        supplyMarketList,
        borrowMarketList,
        poolConfig,
        marketInfo,
        totalSuppliedUSD: totalSuppliedUSD.div(_1e6),
        totalBorrowedUSD: totalBorrowedUSD.div(_1e6),
        walletAllSupplyBalUSD,
        walletAllBorrowBalUSD,
        walletNetAPY: walletNetAPY.isNaN() ? ZERO : walletNetAPY,
        walletBorrowingPowerUSD: userBorrowingPower.div(_1e6),
        walletTotalDebtValueUSD: walletTotalDebtValue.div(_1e6),
        walletCollateralValueUSD: walletCollateralValue.div(_1e6),
        walletLTV: walletLTV.isNaN() ? ZERO : walletLTV,
      })
    }

    taskStore.updateTask(fetchData, TaskNameId.usePool)
    fetchData()
  }, [assets, poolAddr, prices])
}
